import {Box} from "@mui/material";
import {NavigationSheet} from "./NavigationSheet";
import {NavigationSidePanel} from "./NavigationSidePanel";
import {Floorplan, RoomImage} from "../../../api/apiClient";
import {useState} from "react";
import {
    availableNavTabs,
    EmptyNavigation,
    NavigationSections,
    NavigationSwitcher,
    NavTab
} from "./NavigationContent";
import {ViewControls} from "./ViewControls";
import {
    needsTabSwitcher,
    PANEL_GUTTER,
    PANEL_HANDLE_WIDTH_PX,
    PANEL_WIDTH_PX,
    PANEL_WIDTH_SHORT_PX,
    SHEET_HEADER_HEIGHT_PX,
    useNavLayout,
    useSheetContentHeight
} from "./useNavLayout";

// Lifts the whole overlay above the A-Frame scene and the canvas it renders into.
const OVERLAY_Z_INDEX = 1050;

// The grabber at the top of the sheet: the short bar that says the sheet can be dragged.
const SHEET_GRABBER_WIDTH_PX = 32;
const SHEET_GRABBER_HEIGHT_PX = 4;

export interface BrowserUIProps {
    floorPlans: Floorplan[];
    rooms: RoomImage[];
    currentPanorama: string;
    changePanorama: (panorama: string) => void;
}

export function BrowserUI(props: BrowserUIProps) {
    const {currentPanorama, changePanorama, floorPlans, rooms} = props;

    const layout = useNavLayout();
    const sheetContentHeight = useSheetContentHeight(floorPlans);

    // Which section the switcher is on. Kept even in the stacked layout that has no switcher, so that
    // rotating a phone comes back to the section the user was looking at.
    const [activeTab, setActiveTab] = useState<NavTab>("floorplan");
    const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

    // The panel starts open, where the sheet starts closed: it takes the screen edge rather than
    // covering the panorama.
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(true);

    // Held here rather than in the minimap, so switching to Rooms and back does not turn the markers off.
    const [showViewpoints, setShowViewpoints] = useState<boolean>(false);

    const tabs = availableNavTabs(floorPlans, rooms);
    // The stored tab can be one this tour does not have.
    const activeAvailableTab = tabs.includes(activeTab) ? activeTab : tabs[0];
    const visibleTabs = needsTabSwitcher(layout) ? [activeAvailableTab] : tabs;
    const showSwitcher = needsTabSwitcher(layout) && tabs.length > 1;

    const panelWidth = layout === "panel-switched" ? PANEL_WIDTH_SHORT_PX : PANEL_WIDTH_PX;

    const sections = tabs.length === 0
        ? <EmptyNavigation/>
        : <NavigationSections floorPlans={floorPlans} rooms={rooms} currentPanorama={currentPanorama}
                              changePanorama={changePanorama} layout={layout} visibleTabs={visibleTabs}
                              showViewpoints={showViewpoints} setShowViewpoints={setShowViewpoints}/>;

    const switcher = showSwitcher
        ? <NavigationSwitcher tabs={tabs} activeTab={activeAvailableTab} setActiveTab={setActiveTab}/>
        : undefined;

    // The controls clear the handle as well as the panel. Clearing only the panel leaves them sharing a
    // strip of screen with it, which shows up once the viewport is short enough for the two to meet.
    const controlsOffsetPx = layout === "sheet"
        ? 0
        : (isPanelOpen ? panelWidth : 0) + PANEL_HANDLE_WIDTH_PX;

    // The overlay ignores pointer events so dragging the panorama still works between the controls;
    // each interactive piece opts back in. It also clips, so the closed panel parked beyond the right
    // edge cannot give the page something to scroll sideways to.
    return <Box sx={{position: "absolute", inset: 0, userSelect: "none", pointerEvents: "none",
        overflow: "hidden", zIndex: OVERLAY_Z_INDEX}}>

        <ViewControls rightOffsetPx={controlsOffsetPx}/>

        {layout === "sheet"
            ? <NavigationSheet headerHeight={SHEET_HEADER_HEIGHT_PX}
                               contentHeight={sheetContentHeight}
                               isOpen={isSheetOpen}
                               setIsOpen={setIsSheetOpen}
                               header={<>
                                   <Box sx={{
                                       width: SHEET_GRABBER_WIDTH_PX,
                                       height: SHEET_GRABBER_HEIGHT_PX,
                                       // Half the bar's height rounds its ends into a pill. Spelled in
                                       // px, as a bare number here would be theme spacing instead.
                                       borderRadius: `${SHEET_GRABBER_HEIGHT_PX / 2}px`,
                                       backgroundColor: "divider"
                                   }}/>
                                   {/* Stretches into the header's own padding; see NavigationSheet. */}
                                   {switcher && <Box sx={{alignSelf: "stretch"}}>{switcher}</Box>}
                               </>}>
                {sections}
            </NavigationSheet>

            : <NavigationSidePanel width={panelWidth} isOpen={isPanelOpen} setIsOpen={setIsPanelOpen}>
                {switcher && <Box sx={{px: PANEL_GUTTER, pt: 2, pb: 1}}>{switcher}</Box>}
                {sections}
            </NavigationSidePanel>}
    </Box>
}
